/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import Shared from 'screens/shared';
import { useSelector } from 'react-redux';
import { actions, ACTIONS_TYPES } from 'redux/local';
import {
  AUTO_SCALING_POLICY_ACTION_AMOUNT_TYPE,
  AUTO_SCALING_POLICY_METRIC,
  AUTO_SCALING_POLICY_METRIC_UNITS,
  AUTO_SCALING_TERMINATION_POLICIES,
  AVAILABILITY_ZONE,
  AVAILABILITY_ZONE$, RESOURCE_TYPE,
  STORAGE_TYPE,
  VALIDATION,
} from 'constant';
import { CLOUD_INIT } from 'constant/cloud-init';

import {
  objectToOptions, _tf, _t, enumToArray, resourceIconMap, getCPUFamilyOptions, calculateMinSizeForImage,
} from 'utils';
import { formField } from 'types';
import { nicFields, validations as nicValidations } from './nic-fields';

const VOLUME_PUFFER = 2;

export default ({ isCancel = false, onCancel, group = {} }) => {
  const { id, ...restGroup } = group;
  const { [ACTIONS_TYPES.DATA_CENTER.GET_ALL]: dataCenters = [] } = useSelector((e) => e);
  const {
    [ACTIONS_TYPES.LOCATION.GET_ALL]: locationsGlobal = [],
  } = useSelector((e) => e);
  const [model, setModel] = useState({
    name: '',
    datacenter: {},
    policy: {
      metric: 'INSTANCE_CPU_UTILIZATION_AVERAGE',
      scaleInAction: { amountType: 'ABSOLUTE' },
      scaleOutAction: { amountType: 'ABSOLUTE' },
    },
    replicaConfiguration: {
      nics: [],
      volumes: [],
      ram: 1024,
    },
    ...restGroup,
  });
  const [selectedImage, setSelectedImage] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(false);

  useEffect(() => {
    actions.location.getAll();
  }, []);

  useEffect(() => {
    const currentDataCenter = dataCenters.find((item) => item.id === model.datacenter?.id);
    const locationId = currentDataCenter?.properties.location;
    const location = locationId && locationsGlobal.find((item) => item.id === locationId);
    if (location) {
      setSelectedLocation(location);
    }
  }, [model.datacenter, locationsGlobal, dataCenters]);

  let cpuArchitectures = [];
  if (selectedLocation) {
    const { properties = {} } = selectedLocation;
    cpuArchitectures = properties.cpuArchitecture;
  }

  const selectedCpuArch = cpuArchitectures.find((e) => e.cpuFamily === model.replicaConfiguration.cpuFamily);
  let coresRange = [1, 10];
  let ramRange = [1, 20];

  if (selectedCpuArch) {
    coresRange = [1, selectedCpuArch.maxCores || 10];
    ramRange = [1, parseInt(selectedCpuArch.maxRam / 1024, 10) || 20];
  }

  const commonLabelStyle = {
    paddingTop: 10,
    fontSize: 16,
    color: 'gray',
    display: 'block',
  };

  const minSize = calculateMinSizeForImage(selectedImage, VOLUME_PUFFER);

  const volumeValidations = [
    {
      name: 'name',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'size',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'size',
      type: VALIDATION.NUMBER,
      min: minSize,
    },
    {
      name: 'type',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'image',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'imagePassword',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'imagePassword',
      type: VALIDATION.STRING,
      min: 8,
      max: 50,
    },
    {
      name: 'imagePassword',
      type: VALIDATION.REGEX,
      regex: /^[0-9a-zA-Z]{8,50}$/,
      message: _t('COMMON.VALIDATION_TEXTS.PASSWORD_LATIN_NUMBER_VALIDATION'),
    },
  ];

  const fields = [
    formField.text({
      name: 'name',
      title: 'Name',
    }),
    formField.label({
      name: 'datacenterLabel',
      title: (
        <span style={commonLabelStyle}>
          Data Center
        </span>
      ),
      helpText: _t('AUTO_SCALE.GROUPS.HELP_TEXTS.DATA_CENTER'),
    }),
    formField.datacenter({
      name: 'datacenter',
      title: 'Data Center',
      setValueTo: (e) => e.id,
      setValueFrom: (value = {}) => ({ id: value.id }),
      onChange: (value) => setModel({ ...model, datacenter: value }),
    },
    {
      name: 'targetReplicaCountLabel',
      title: (
        <span style={commonLabelStyle}>
          {_t('AUTO_SCALE.GROUPS.FIELDS.targetReplicaCount')}
        </span>
      ),
      helpText: _t('AUTO_SCALE.GROUPS.HELP_TEXTS.TARGET_REPLICA_COUNT'),
    }),
    formField.text({
      name: 'targetReplicaCount',
      title: _t('AUTO_SCALE.GROUPS.FIELDS.targetReplicaCount'),
    }),
    formField.text({
      name: 'minReplicaCount',
      title: _t('AUTO_SCALE.GROUPS.FIELDS.minReplicaCount'),
      helpText: _t('AUTO_SCALE.GROUPS.HELP_TEXTS.MIN_REPLICA_COUNT'),
    }),
    formField.text({
      name: 'maxReplicaCount',
      title: _t('AUTO_SCALE.GROUPS.FIELDS.maxReplicaCount'),
      helpText: _t('AUTO_SCALE.GROUPS.HELP_TEXTS.MAX_REPLICA_COUNT'),
    }),
    formField.object({
      name: 'policy',
      title: (
        <span style={{
          paddingTop: 10,
          marginTop: 5,
          color: 'gray',
          display: 'block',
        }}
        >
          {_t('AUTO_SCALE.GROUPS.FIELDS.policy')}
        </span>
      ),
      helpText: _t('AUTO_SCALE.GROUPS.HELP_TEXTS.POLICY'),
      validations: [
        {
          name: 'scaleInThreshold',
          type: VALIDATION.NUMBER,
          min: 1,
          compareName: 'scaleOutThreshold',
          delta: -40,
          equals: true,
          message: _tf('AUTO_SCALE.GROUPS.VALIDATION_TEXTS.SCALE_OUT_DELTA', { value: '40' }),
        },
        {
          name: 'scaleOutThreshold',
          type: VALIDATION.NUMBER,
          max: 1,
          compareName: 'scaleInThreshold',
          delta: 40,
          equals: true,
          message: _tf('AUTO_SCALE.GROUPS.VALIDATION_TEXTS.SCALE_IN_DELTA', { value: '40' }),
        },
        {
          name: 'scaleInThreshold',
          type: VALIDATION.REQUIRED,
        },
        {
          name: 'scaleInThreshold',
          type: VALIDATION.NUMBER,
        },
        {
          name: 'scaleOutThreshold',
          type: VALIDATION.REQUIRED,
        },
        {
          name: 'scaleOutThreshold',
          type: VALIDATION.NUMBER,
        },
        {
          name: 'range',
          type: VALIDATION.DURATION,
        },
        {
          name: 'range',
          type: VALIDATION.REQUIRED,
        },
        {
          name: 'unit',
          type: VALIDATION.REQUIRED,
        },
      ],
      fields: [
        formField.dropdown({
          name: 'metric',
          title: _t('AUTO_SCALE.GROUPS.FIELDS.policyMetric'),
          options: objectToOptions(AUTO_SCALING_POLICY_METRIC),
          helpText: _t('AUTO_SCALE.GROUPS.HELP_TEXTS.POLICY_METRIC'),
        }),
        formField.text({
          name: 'scaleInThreshold',
          title: _t('AUTO_SCALE.GROUPS.FIELDS.policyScaleInThreshold'),
          helpText: _t('AUTO_SCALE.GROUPS.HELP_TEXTS.SCALE_IN_THRESHOLD'),
        }),
        formField.text({
          name: 'scaleOutThreshold',
          title: _t('AUTO_SCALE.GROUPS.FIELDS.policyScaleOutThreshold'),
          helpText: _t('AUTO_SCALE.GROUPS.HELP_TEXTS.SCALE_OUT_THRESHOLD'),
        }),
        formField.text({
          name: 'range',
          title: _t('AUTO_SCALE.GROUPS.FIELDS.policyRange'),
          helpText: _t('AUTO_SCALE.GROUPS.HELP_TEXTS.POLICY_RANGE'),
        }),
        formField.dropdown({
          name: 'unit',
          title: _t('AUTO_SCALE.GROUPS.FIELDS.policyUnit'),
          options: objectToOptions(AUTO_SCALING_POLICY_METRIC_UNITS),
          helpText: _t('AUTO_SCALE.GROUPS.HELP_TEXTS.POLICY_UNIT'),
        }),
        formField.object({
          name: 'scaleInAction',
          type: 'object',
          title: (
            <span style={{
              paddingTop: 10,
              fontSize: 18,
              color: 'var(--color-base)',
              display: 'block',
            }}
            >
              {_t('AUTO_SCALE.GROUPS.FIELDS.scaleInAction')}
            </span>
          ),
          helpText: _t('AUTO_SCALE.GROUPS.HELP_TEXTS.SCALE_IN_ACTION'),
          validations: [
            {
              name: 'amount',
              type: VALIDATION.REQUIRED,
            },
            {
              name: 'amount',
              type: VALIDATION.NUMBER,
            },
            {
              name: 'amountType',
              type: VALIDATION.REQUIRED,
            },
            {
              name: 'cooldownPeriod',
              type: VALIDATION.DURATION,
            },
          ],
          fields: [
            formField.dropdown({
              name: 'amountType',
              title: _t('AUTO_SCALE.GROUPS.FIELDS.policyScaleInAmountType'),
              options: objectToOptions(AUTO_SCALING_POLICY_ACTION_AMOUNT_TYPE),
            }),
            formField.label({
              name: 'policyScaleInAmountLabel',
              title: (
                <span style={commonLabelStyle}>
                  {_t('AUTO_SCALE.GROUPS.FIELDS.policyScaleInAmount')}
                </span>
              ),
              helpText: _t('AUTO_SCALE.GROUPS.HELP_TEXTS.AMOUNT'),
            }),
            formField.text({
              name: 'amount',
              title: _t('AUTO_SCALE.GROUPS.FIELDS.policyScaleInAmount'),
            }),
            formField.label({
              name: 'policyScaleInCooldownPeriodLabel',
              title: (
                <span style={commonLabelStyle}>
                  {_t('AUTO_SCALE.GROUPS.FIELDS.policyScaleInCooldownPeriod')}
                </span>
              ),
              helpText: _t('AUTO_SCALE.GROUPS.HELP_TEXTS.COOL_DOWN'),
            }),
            formField.text({
              name: 'cooldownPeriod',
              placeholder: '5m',
              title: _t('AUTO_SCALE.GROUPS.FIELDS.policyScaleInCooldownPeriod'),
            }),
            formField.dropdown({
              name: 'terminationPolicy',
              title: _t('AUTO_SCALE.GROUPS.FIELDS.terminationPolicy'),
              options: objectToOptions(AUTO_SCALING_TERMINATION_POLICIES),
            }),
          ],
        }),
        formField.object({
          name: 'scaleOutAction',
          title: (
            <span style={{
              paddingTop: 10,
              fontSize: 18,
              color: 'var(--color-base)',
              display: 'block',
            }}
            >
              {_t('AUTO_SCALE.GROUPS.FIELDS.scaleOutAction')}
            </span>
          ),
          helpText: _t('AUTO_SCALE.GROUPS.HELP_TEXTS.SCALE_OUT_ACTION'),
          validations: [
            {
              name: 'amount',
              type: VALIDATION.REQUIRED,
            },
            {
              name: 'amount',
              type: VALIDATION.NUMBER,
            },
            {
              name: 'amountType',
              type: VALIDATION.REQUIRED,
            },
            {
              name: 'cooldownPeriod',
              type: VALIDATION.DURATION,
            },
          ],
          fields: [
            formField.dropdown({
              name: 'amountType',
              title: _t('AUTO_SCALE.GROUPS.FIELDS.policyScaleOutAmountType'),
              options: objectToOptions(AUTO_SCALING_POLICY_ACTION_AMOUNT_TYPE),
            }),
            formField.label({
              name: 'policyScaleOutAmountLabel',
              title: (
                <span style={commonLabelStyle}>
                  {_t('AUTO_SCALE.GROUPS.FIELDS.policyScaleOutAmount')}
                </span>
              ),
              helpText: _t('AUTO_SCALE.GROUPS.HELP_TEXTS.AMOUNT'),
            }),
            formField.text({
              name: 'amount',
              title: _t('AUTO_SCALE.GROUPS.FIELDS.policyScaleOutAmount'),
            }),
            formField.label({
              name: 'policyScaleOutCooldownPeriodLabel',
              title: (
                <span style={commonLabelStyle}>
                  {_t('AUTO_SCALE.GROUPS.FIELDS.policyScaleOutCooldownPeriod')}
                </span>
              ),
              helpText: _t('AUTO_SCALE.GROUPS.HELP_TEXTS.COOL_DOWN'),
            }),
            formField.text({
              name: 'cooldownPeriod',
              placeholder: '5m',
              title: _t('AUTO_SCALE.GROUPS.FIELDS.policyScaleOutCooldownPeriod'),
            }),
          ],
        }),
      ],
    }),
    formField.object({
      name: 'replicaConfiguration',
      title: _t('AUTO_SCALE.GROUPS.FIELDS.replicaConfiguration'),
      helpText: _t('AUTO_SCALE.GROUPS.HELP_TEXTS.REPLICA_CONFIGURATION'),
      validations: [
        {
          name: 'name',
          type: VALIDATION.REQUIRED,
        },
        {
          name: 'cpuFamily',
          type: VALIDATION.REQUIRED,
        },
        {
          name: 'cores',
          type: VALIDATION.REQUIRED,
        },
        {
          name: 'cores',
          type: VALIDATION.NUMBER,
        },
        {
          name: 'ram',
          type: VALIDATION.REQUIRED,
        },
        {
          name: 'ram',
          type: VALIDATION.NUMBER,
        },
        {
          name: 'availabilityZone',
          type: VALIDATION.REQUIRED,
        },
      ],
      fields: [
        formField.dropdown({
          name: 'cpuFamily',
          title: _t('AUTO_SCALE.REPLICA_CONFIG.FIELDS.cpuFamily'),
          options: getCPUFamilyOptions(cpuArchitectures),
        }),
        formField.slider({
          name: 'cores',
          title: _t('AUTO_SCALE.REPLICA_CONFIG.FIELDS.cores'),
          showValue: true,
          options: [4, 8, 16].filter((item) => item < Math.max(...coresRange) && item > Math.min(...coresRange)),
        }),
        formField.slider({
          name: 'ram',
          title: 'RAM',
          showValue: true,
          inputFn: (value) => value / 1024,
          outputFn: (value) => value * 1024,
          options: [4, 8, 16, 32, 64].filter((item) => item < Math.max(...ramRange) && item > Math.min(...ramRange)),
        }),
        formField.dropdown({
          name: 'availabilityZone',
          title: _t('AUTO_SCALE.REPLICA_CONFIG.FIELDS.availabilityZone'),
          options: [...enumToArray(AVAILABILITY_ZONE).map((e) => [e, AVAILABILITY_ZONE$[e]])],
        }),
        formField.array({
          name: 'nics',
          icon: resourceIconMap(RESOURCE_TYPE.NIC),
          title: 'NICs',
          fields: nicFields,
          validations: nicValidations,
          isAddButton: true,

          setItemTitle: (e) => `NIC ${e.name}`,
          setItemDetails: (e) => `LAN: ${e.lan} DHCP: ${e.dhcp ? '✓' : '✗'}`,
          actionButtonName: 'Add NIC',
        }),
        formField.array({
          name: 'volumes',
          icon: resourceIconMap(RESOURCE_TYPE.VOLUME),
          onChange: () => setSelectedImage(false),
          onEdit: (value) => {
            if (value.image) {
              setSelectedImage(value.image);
            }
          },
          onEditCancel: () => {
            setSelectedImage(false);
          },
          setItemTitle: (e) => e.name,
          setItemDetails: (e) => (
            <div>
              <div>
                {e.size}
                GB
                <span> </span>
                {e.type}
              </div>
              <div>
                {e.image?.name || ''}
              </div>
            </div>
          ),
          initValues: {
            size: 1,
            type: 'HDD',
          },
          title: _t('Storages'),
          fields: [
            formField.text({
              name: 'name',
              title: _t('Name'),
            }),
            formField.dropdown({
              name: 'type',
              title: _t('AUTO_SCALE.REPLICA_CONFIG.FIELDS.volumes.type'),
              options: [...objectToOptions(STORAGE_TYPE, [STORAGE_TYPE.DAS])],
            }),
            formField.number({
              name: 'size',
              title: _tf('AUTO_SCALE.REPLICA_CONFIG.FIELDS.volumes.size', { min: VOLUME_PUFFER }),
              helpText: _tf('AUTO_SCALE.REPLICA_CONFIG.HELP_TEXTS.VOLUME_SIZE', { min: VOLUME_PUFFER }),
            }),
            formField.hdd({
              name: 'image',
              onChange: (value) => setSelectedImage(value),
            }),
            formField.password({
              name: 'imagePassword',
              title: _t('AUTO_SCALE.REPLICA_CONFIG.FIELDS.volumes.imagePassword'),
              visible: false,
            }),
            formField.textarea({
              name: 'sshKeys',
              title: _t('AUTO_SCALE.REPLICA_CONFIG.FIELDS.volumes.sshKeys'),
            }),
            formField.userData({
              name: 'userData',
              title: _t('AUTO_SCALE.REPLICA_CONFIG.FIELDS.volumes.userData'),
              visible: false,
            }),
          ],
          validations: volumeValidations,
        }),
      ],
    }),
  ];

  const validations = [
    {
      name: 'name',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'name',
      type: VALIDATION.STRING,
      max: 255,
    },
    {
      name: 'datacenter',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'targetReplicaCount',
      type: VALIDATION.NUMBER,
      min: 0,
      max: 100,
    },
    {
      name: 'minReplicaCount',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'minReplicaCount',
      type: VALIDATION.NUMBER,
      min: 0,
      max: 99,
    },
    {
      name: 'maxReplicaCount',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'maxReplicaCount',
      type: VALIDATION.NUMBER,
      min: 0,
      max: 100,
    },
    {
      name: 'minReplicaCount',
      type: VALIDATION.NUMBER,
      min: 1,
      compareName: 'maxReplicaCount',
      equals: false,
      message: _tf('COMMON.VALIDATION_TEXTS.VALUE_LT', { value: _t('AUTO_SCALE.GROUPS.FIELDS.maxReplicaCount') }),
    },
    {
      name: 'maxReplicaCount',
      type: VALIDATION.NUMBER,
      max: 1,
      equals: false,
      compareName: 'minReplicaCount',
      message: _tf('COMMON.VALIDATION_TEXTS.VALUE_GT', { value: _t('AUTO_SCALE.GROUPS.FIELDS.minReplicaCount') }),
    },
    {
      name: 'maxReplicaCount',
      type: VALIDATION.NUMBER,
      max: 1,
      equals: true,
      compareName: 'targetReplicaCount',
      message: _tf('COMMON.VALIDATION_TEXTS.VALUE_GTE', { value: _t('AUTO_SCALE.GROUPS.FIELDS.targetReplicaCount') }),
    },
    {
      name: 'targetReplicaCount',
      type: VALIDATION.NUMBER,
      max: 1,
      equals: true,
      compareName: 'minReplicaCount',
      message: _tf('COMMON.VALIDATION_TEXTS.VALUE_GTE', { value: _t('AUTO_SCALE.GROUPS.FIELDS.minReplicaCount') }),
    },
    {
      name: 'targetReplicaCount',
      type: VALIDATION.NUMBER,
      min: 1,
      equals: false,
      compareName: 'maxReplicaCount',
      message: _tf('COMMON.VALIDATION_TEXTS.VALUE_LT', { value: _t('AUTO_SCALE.GROUPS.FIELDS.maxReplicaCount') }),
    },

  ];

  const volumeChangeFields = [
    {
      name: 'size',
      props: {
        min: minSize,
      },
    },
    {
      name: 'imagePassword',
      props: {
        visible: !!selectedImage.licenceType || !!selectedImage.id,
      },
    },
    {
      name: 'sshKeys',
      props: {
        visible: selectedImage.licenceType ? selectedImage.licenceType === 'LINUX' : !!selectedImage.id || false,
      },
    },
    {
      name: 'image',
      props: {
        locationId: selectedLocation?.id,
        isOpen: !!selectedImage.id,
      },
    },
    {
      name: 'userData',
      props: {
        visible: selectedImage.cloudInit === CLOUD_INIT.V1,
        // && (selectedImage.defaultImage || false), // currentContract),
      },
    },
  ];

  const onChangeForm = (value) => {
    setModel(value);
  };

  const onSave = (properties) => {
    properties.datacenter = { id: properties.datacenter.id };
    properties.replicaConfiguration.volumes = properties.replicaConfiguration.volumes.map((item) => {
      item.image = item.image.id;
      return item;
    });

    if (id) {
      if (onCancel) onCancel();
      return actions.autoScaling.group.update({ id, properties });
    }

    return actions.autoScaling.group.create(properties);
  };

  return (
    <>
      <Shared.forms.Form
        fields={fields}
        fieldsValues={model}
        onProps={model}
        onSubmit={onSave}
        isCancel={isCancel}
        onCancel={onCancel}
        onChange={onChangeForm}
        validations={validations}
        changeFields={[
          {
            name: 'volumes',
            props: { validations: volumeValidations },
          },
          {
            name: 'cpuFamily',
            props: { options: getCPUFamilyOptions(cpuArchitectures) },
          },
          {
            name: 'cores',
            props: { range: coresRange },
          },
          {
            name: 'ram',
            props: { range: ramRange },
          },
          ...volumeChangeFields,
        ]}
      />
    </>
  );
};
